import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@/components/theme-provider';
import { MessageSquarePlus } from 'lucide-react';
import { FeedbackModal } from '@/components/ui/FeedbackModal';

interface NavbarProps {
    variant?: 'landing' | 'auth' | 'minimal';
}

export function Navbar({ variant = 'landing' }: NavbarProps) {
    const { theme, setTheme } = useTheme();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const location = useLocation();

    const toggleDarkMode = () => {
        setTheme(theme === 'dark' ? 'light' : 'dark');
    };

    const isAuthPage = variant === 'auth' || variant === 'minimal';
    const isWorkingPage = location.pathname === '/how-it-works';
    const isLoginPage = location.pathname === '/login' || location.pathname === '/auth/login';
    const isRegisterPage = location.pathname === '/register' || location.pathname.startsWith('/auth/register') || location.pathname === '/register-investor' || location.pathname === '/register-startup';

    return (
        <nav className="sticky top-0 z-50 w-full border-b border-slate-200 dark:border-slate-800 bg-surface-light/80 dark:bg-surface-dark/80 backdrop-blur-md">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    <Link to="/" className="flex-shrink-0 flex items-center gap-2 cursor-pointer">
                        <img
                            alt="uniscout Logo"
                            className="h-10 w-auto"
                            src="/logo.png"
                        />
                    </Link>

                    {/* Right-aligned Group (Desktop & Mobile Utilities) */}
                    <div className="flex items-center gap-4 sm:gap-8">
                        {/* Desktop Menu Group */}
                        <div className="hidden md:flex items-center gap-8">
                            <div className="flex items-center space-x-8">
                                {variant === 'landing' && (
                                    <>
                                        {isWorkingPage ? (
                                            <Link className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-primary transition-colors" to="/">Home</Link>
                                        ) : (
                                            <a className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-primary transition-colors" href="/#features">Features</a>
                                        )}

                                        {!isWorkingPage && (
                                            <Link className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-primary transition-colors" to="/how-it-works">How it works</Link>
                                        )}

                                        <a className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-primary transition-colors" href="/#pricing">Pricing</a>
                                    </>
                                )}

                                {variant === 'auth' && (
                                    <Link className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-primary transition-colors" to="/">Back to Home</Link>
                                )}
                            </div>

                            {/* Desktop Action Buttons */}
                            <div className="flex items-center gap-4">
                                {(variant === 'landing' || isLoginPage) && (
                                    <Link to="/register">
                                        <button className="bg-primary hover:bg-indigo-600 text-white px-5 py-2 rounded-full text-sm font-medium transition-all shadow-lg hover:shadow-indigo-500/30">
                                            {isLoginPage ? 'Register' : 'Get Started'}
                                        </button>
                                    </Link>
                                )}

                                {(isRegisterPage || (variant === 'minimal' && !isLoginPage)) && (
                                    <Link to="/login">
                                        <button className="bg-primary hover:bg-indigo-600 text-white px-5 py-2 rounded-full text-sm font-medium transition-all shadow-lg hover:shadow-indigo-500/30">
                                            Login
                                        </button>
                                    </Link>
                                )}
                            </div>
                        </div>

                        {/* Utilities & Mobile Actions */}
                        <div className="flex items-center gap-2 sm:gap-4">
                            {/* Mobile Action Buttons */}
                            <div className="md:hidden flex items-center gap-2">
                                {(variant === 'landing' || isLoginPage) && (
                                    <Link to="/register">
                                        <button className="bg-primary hover:bg-indigo-600 text-white px-3 py-1.5 rounded-full text-xs font-medium transition-all shadow-lg">
                                            {isLoginPage ? 'Register' : 'Get Started'}
                                        </button>
                                    </Link>
                                )}

                                {(isRegisterPage || (variant === 'minimal' && !isLoginPage)) && (
                                    <Link to="/login">
                                        <button className="bg-primary hover:bg-indigo-600 text-white px-3 py-1.5 rounded-full text-xs font-medium transition-all shadow-lg">
                                            Login
                                        </button>
                                    </Link>
                                )}
                            </div>

                            {/* Feedback Button */}
                            {!isAuthPage && !isWorkingPage && !isLoginPage && !isRegisterPage && (
                                <button
                                    onClick={() => setIsFeedbackOpen(true)}
                                    className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/50 rounded-full transition-colors border border-indigo-100 dark:border-indigo-800"
                                >
                                    <MessageSquarePlus className="w-3.5 h-3.5" />
                                    Share Feedback
                                </button>
                            )}

                            <button
                                className="p-1.5 sm:p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400"
                                onClick={toggleDarkMode}
                            >
                                <span className="material-icons text-lg sm:text-xl">{theme === 'dark' ? 'light_mode' : 'brightness_4'}</span>
                            </button>

                            {/* Mobile Menu Button - Only for landing variant */}
                            {variant === 'landing' && (
                                <button
                                    className="md:hidden text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 p-1.5"
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                >
                                    <span className="material-icons">{mobileMenuOpen ? 'close' : 'menu'}</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            {variant === 'landing' && mobileMenuOpen && (
                <div className="md:hidden bg-surface-light dark:bg-surface-dark border-t border-slate-200 dark:border-slate-800">
                    <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                        {isWorkingPage ? (
                            <Link className="block px-3 py-2 rounded-md text-base font-medium text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-primary hover:bg-slate-50 dark:hover:bg-slate-800" to="/">Home</Link>
                        ) : (
                            <a className="block px-3 py-2 rounded-md text-base font-medium text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-primary hover:bg-slate-50 dark:hover:bg-slate-800" href="/#features">Features</a>
                        )}

                        {!isWorkingPage && (
                            <Link className="block px-3 py-2 rounded-md text-base font-medium text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-primary hover:bg-slate-50 dark:hover:bg-slate-800" to="/how-it-works">How it works</Link>
                        )}

                        <a className="block px-3 py-2 rounded-md text-base font-medium text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-primary hover:bg-slate-50 dark:hover:bg-slate-800" href="/#pricing">Pricing</a>

                        {/* Mobile menu action button - redundant with header button but good for UX if menu is large */}
                        <div className="px-3 py-2">
                            <Link to={isLoginPage ? "/register" : (isRegisterPage ? "/login" : "/register")} className="block w-full text-center bg-primary hover:bg-indigo-600 text-white px-5 py-3 rounded-full text-base font-medium transition-all shadow-lg">
                                {isLoginPage ? 'Register' : (isRegisterPage ? 'Login' : 'Get Started')}
                            </Link>
                        </div>
                        <button
                            className="w-full text-left px-3 py-2 text-slate-500 dark:text-slate-400 flex items-center gap-2"
                            onClick={toggleDarkMode}
                        >
                            <span className="material-icons text-xl">{theme === 'dark' ? 'light_mode' : 'brightness_4'}</span>
                            <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                        </button>

                        {!isAuthPage && !isWorkingPage && !isLoginPage && !isRegisterPage && (
                            <button
                                className="w-full text-left px-3 py-2 text-indigo-600 dark:text-indigo-300 flex items-center gap-2 font-medium"
                                onClick={() => {
                                    setIsFeedbackOpen(true);
                                    setMobileMenuOpen(false);
                                }}
                            >
                                <MessageSquarePlus className="w-5 h-5 text-indigo-500" />
                                <span>Share Feedback</span>
                            </button>
                        )}
                    </div>
                </div>
            )}

            <FeedbackModal
                open={isFeedbackOpen}
                onOpenChange={setIsFeedbackOpen}
                page="Navbar"
            />
        </nav>
    );
}
